import { Component, OnInit, Input, OnChanges, SimpleChanges, ElementRef, EventEmitter, ViewChild } from '@angular/core';
import get from 'lodash-es/get';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Header } from '@finder/shared/services/header-alignment/header-alignment.interface';
import { HeaderAlignmentService } from '@finder/shared/services/header-alignment/header-alignment.service';
import { LottieComponent } from '@finder/shared/components/lottie/lottie.component';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { AnchorComponent } from '../anchor/anchor.component';
import { CommonModule } from '@angular/common';
import { InViewportDirective } from '@finder/shared/directives/in-viewport/in-viewport.directive';
import { FontIconComponent } from '../font-icon/font-icon.component';
import { HorizontalRuleComponent } from '../horizontal-rule/horizontal-rule.component';
import { PictureComponent } from '../picture/picture.component';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';

@Component({
    selector: 'finder-component-header',
    templateUrl: './component-header.component.html',
    styleUrls: ['./component-header.component.scss'],
    standalone: true,
    imports: [
        AnchorComponent,
        CommonModule,
        InViewportDirective,
        FontIconComponent,
        HorizontalRuleComponent,
        LottieComponent,
        PictureComponent,
        SafeHtmlPipe,
    ]
})
export class ComponentHeaderComponent implements OnInit, OnChanges {
    @Input() data: any;
    @Input() hrDecoration: any;
    @ViewChild(LottieComponent) finderLottie: LottieComponent;

    // For in-viewport triggering of lottie
    onPlayback = new EventEmitter();

    model: Header;
    requireFullElement = false;
    addedOffset = 0;
    isVisible = true;

    private viewportTrigger;
    private isMobile: boolean;
    private destroy$ = new Subject<void>();

    constructor(
        private headerAlignmentService: HeaderAlignmentService,
        private el: ElementRef,
        private viewportDetection: ViewportDetectionService
    ) {}

    ngOnInit() {
        this.viewportTrigger = get(this.data, 'viewportTrigger', false);
        this.setModel();
        this.setVisibility();
        this.setLottie();
        this.setFlourish();

        this.viewportDetection.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.setFlourish();
            });
    }

    // eslint-disable-next-line @angular-eslint/use-lifecycle-interface
    ngOnDestroy() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.data.firstChange) {
            return;
        }

        this.setModel();
        this.setLottie();
    }

    inViewport(event) {
        /* istanbul ignore if */
        if (this.isVisible === true) {
            return;
        }

        this.onPlayback.emit(event);
        this.isVisible = event;
    }

    private setModel() {
        this.model = this.headerAlignmentService.getHeaderAlignment(this.data);
    }

    private setLottie() {
        const lottie = get(this.data, 'lottie');
        if (!lottie) {
            return;
        }

        this.requireFullElement = get(lottie, 'requireFullElement');
        this.addedOffset = Number(get(lottie, 'addedOffset', '0'));

        if (lottie.height) {
            this.el.nativeElement.style.setProperty('--ch-header-height', lottie.height);
        }
    }

    private setVisibility() {
        // If we have viewport triggering enabled then we want the visibility
        // of the header to be `false` so we can fade it in when the header
        // comes into the viewport
        this.isVisible =  this.viewportTrigger ? false : true;
    }

    private setFlourish() {
        this.isMobile = this.viewportDetection.isMobile();
        const flourishLeft = (this.isMobile && this.data.flourishMobileLeft) ? this.data.flourishMobileLeft : this.data?.flourishLeft;
        const flourishRight = (this.isMobile && this.data.flourishMobileRight) ? this.data.flourishMobileRight : this.data?.flourishRight;

        this.setFlourishProps('left', flourishLeft);
        this.setFlourishProps('right', flourishRight);
    }

    private setFlourishProps(pos, flourish) {
        if (!flourish) {
            return;
        }

        const styleDomMethod = this.getElementReference().style;

        this.getElementReference().classList.add('flourish-decorator');
        styleDomMethod.setProperty(`--ch-header-flourish-${pos}-url`, flourish.url ? `url("${flourish.url}")` : 'unset');
        styleDomMethod.setProperty(`--ch-header-flourish-${pos}-width`, flourish.width ?? 'auto');
        styleDomMethod.setProperty(`--ch-header-flourish-${pos}-height`, flourish.height ?? 'auto');
        styleDomMethod.setProperty(`--ch-header-flourish-${pos}-offset`, flourish.offset ?? '-10px');
        styleDomMethod.setProperty(`--ch-header-flourish-${pos}-background-size`, 'auto');
    }

    /* istanbul ignore next */
    getElementReference() {
        return this.el.nativeElement;
    }
}
